import React, { useEffect, useRef, useState } from "react";
import * as d3 from "d3";

export function SimulateAbatementGraphD3({ data }) {
  const wrapperRef = useRef(null);
  const svgRef = useRef(null);

  const [size, setSize] = useState({ width: 900, height: 450 });

  // ResizeObserver — adjusts width/height dynamically
  useEffect(() => {
    const resizeObserver = new ResizeObserver((entries) => {
      if (!entries || entries.length === 0) return;
      const { width } = entries[0].contentRect;

      // maintain ~2:1 aspect ratio for large screens
      setSize({
        width: width,
        height: Math.max(350, width * 0.45),
      });
    });

    if (wrapperRef.current) {
      resizeObserver.observe(wrapperRef.current);
    }

    return () => resizeObserver.disconnect();
  }, []);

  // Main D3 render logic
  useEffect(() => {
    // if (!data || data.length === 0) return;
    if(data && data.length > 0) {
      const { width, height } = size;

      const svg = d3.select(svgRef.current);
      svg.selectAll("*").remove();

      const margin = { top: 30, right: 30, bottom: 110, left: 80 };
      const innerWidth = width - margin.left - margin.right;
      const innerHeight = height - margin.top - margin.bottom;

      const g = svg
        .append("g")
        .attr("transform", `translate(${margin.left},${margin.top})`);

      const truncateLabel = (label, max = 10) =>
        label.length > max ? label.slice(0, max) + "…" : label;

      const formatted = data.map((d) => ({
        name: d[0],
        cost: +d[1],
        abatement: +d[2],
      }));

      // formatted.sort((a, b) => a.abatement - b.abatement);
      formatted.sort((a, b) => a.cost - b.cost);
      const totalAbatement = d3.sum(formatted, (d) => Math.abs(d.abatement));

      let cumulative = 0;
      formatted.forEach((d) => {
        d.x0 = cumulative;
        d.width = (Math.abs(d.abatement) / totalAbatement) * innerWidth;
        cumulative += d.width;
      });

      const yMin = Math.min(0, d3.min(formatted, (d) => d.cost));
      const yMax = Math.max(0, d3.max(formatted, (d) => d.cost));
      const y = d3.scaleLinear().domain([yMin, yMax]).range([innerHeight, 0]);

      const x = d3.scaleLinear().domain([0, totalAbatement]).range([0, innerWidth]);

      const positiveColor = d3
        .scaleLinear()
        .domain([0, d3.max(formatted, (d) => d.abatement)])
        .range(["#85B9FD", "#0A558E"]);

      const negativeColor = d3
        .scaleLinear()
        .domain([d3.min(formatted, (d) => d.abatement), 0])
        .range(["#E57373", "#C62828"]);

      // Tooltip
      const tooltip = d3
        .select("body")
        .append("div")
        .style("position", "absolute")
        .style("background", "#FFF")
        .style("color", "#1E1E1E")
        .style("padding", "8px 12px")
        .style("border-radius", "6px")
        .style("font-size", "12px")
        .style("box-shadow", "0 2px 6px rgba(0,0,0,0.4)")
        .style("pointer-events", "none")
        .style("opacity", 0);

      // Bars
      g.selectAll("rect")
        .data(formatted)
        .enter()
        .append("rect")
        .attr("x", (d) => d.x0)
        .attr("width", (d) => Math.max(2, d.width - 1))
        .attr("y", (d) => (d.cost >= 0 ? y(d.cost) : y(0)))
        .attr("height", (d) => Math.abs(y(d.cost) - y(0)))
        .attr("fill", (d) =>
          d.abatement >= 0 ? positiveColor(d.abatement) : negativeColor(d.abatement)
        )
        .on("mouseover", function (event, d) {
          d3.select(this).attr("opacity", 0.85);
          tooltip
            .style("opacity", 1)
            .html(
              `<strong>${d.name}</strong><br/>
              Average annual abatement: <b>${d3.format(".2f")(d.abatement)}</b> tCO₂e p.a.<br/>
              Marginal abatement cost: <b>${d3.format(",.2f")(d.cost)}</b> $`
            );
        })
        // .on("mousemove", function (event) {
        //   tooltip
        //     .style("left", event.pageX + 15 + "px")
        //     .style("top", event.pageY - 35 + "px");
        // })
        .on("mousemove", function (event) {
          const tooltipWidth = tooltip.node().offsetWidth;
          const tooltipHeight = tooltip.node().offsetHeight;

          let left = event.pageX + 15;
          let top = event.pageY - 35;

          // Prevent tooltip from going outside the right edge
          if (left + tooltipWidth > window.innerWidth) {
            left = event.pageX - tooltipWidth - 15;
          }

          // Prevent tooltip from going above the top
          if (top < 0) {
            top = event.pageY + 15;
          }

          tooltip
            .style("left", left + "px")
            .style("top", top + "px");
        })
        .on("mouseout", function () {
          d3.select(this).attr("opacity", 1);
          tooltip.style("opacity", 0);
        });

      // Cost labels
      // g.selectAll(".bar-label")
      //   .data(formatted)
      //   .enter()
      //   .append("text")
      //   .attr("class", "bar-label")
      //   .attr("x", (d) => d.x0 + d.width / 2)
      //   .attr("y", (d) => (d.cost < 0 ? y(d.cost) - 8 : y(d.cost) + 15))
      //   .attr("text-anchor", "middle")
      //   .attr("fill", "#fff")
      //   .style("font-size", "12px")
      //   .text((d) => d3.format(",.1f")(d.cost))
      //   .style("display", (d) => (d.width < 40 ? "none" : "block"));

      // X Labels - Project names
      // const xLabels = g
      //   .selectAll(".xLabel")
      //   .data(formatted)
      //   .enter()
      //   .append("text")
      //   .attr("class", "xLabel")
      //   .attr("x", (d) => d.x0 + d.width / 2)
      //   .attr("y", innerHeight + 35)
      //   .attr("text-anchor", "start")
      //   .attr(
      //     "transform",
      //     (d) => `rotate(45, ${d.x0 + d.width / 2}, ${innerHeight + 35})`
      //   )
      //   .style("font-size", "12px")
      //   .attr("fill", "#ccc")
      //   .text((d) => truncateLabel(d.name, 10));
      // xLabels.append("title").text((d) => d.name);

      // Y-axis
      g.append("g")
        .call(
          d3
            .axisLeft(y)
            .ticks(6)
            .tickSize(0)
            .tickFormat((d) => d3.format(",")(d))
        )
        .call((g) => g.select(".domain").remove())
        .selectAll("text")
        .attr("fill", "#ccc")
        .style("font-size", "12px");

      g.append("line")
        .attr("x1", 0)
        .attr("x2", innerWidth)
        .attr("y1", y(0))
        .attr("y2", y(0))
        .attr("stroke", "#ccc")
        .attr("stroke-width", 1.2)
        .attr("opacity", 0.8);

      // Y label
      g.append("text")
        .attr("x", -innerHeight / 2)
        .attr("y", -60)
        .attr("text-anchor", "middle")
        .attr("fill", "#ccc")
        .attr("transform", "rotate(-90)")
        .style("font-size", "13px")
        .text("Cost of abatement ($/tCO₂e)");

      // X label
      g.append("text")
        .attr("x", innerWidth / 2)
        .attr("y", innerHeight + 50)
        .attr("text-anchor", "middle")
        .attr("fill", "#ccc")
        .style("font-size", "13px")
        .text("Average annual abatement (tCO₂e p.a.)");

    }    
  }, [data, size]);

  return (
    <div
      ref={wrapperRef}
      style={{
        width: "100%",
        height: "auto",
      }}
    >
      <svg
        ref={svgRef}
        width={size.width}
        height={size.height + 20}
        style={{ background: "transparent" }}
      ></svg>
    </div>
  );
}